import Carousel, { CarouselItem } from "@/components/common/Carousel";
import OverlayStarToggle from "@/components/common/OverlayStarToggle";
import { useCanvas } from "@/hooks/useCanvas";
import { useVotingStatus } from "@/hooks/useVotingStatus";
import { useCanvasLayout, useCanvasParentPath, useCanvasSubtitle, useCanvasTitle } from "@/layouts/CanvasLayoutContext";
import { routes } from "@/routes/paths";
import { createTopicId, SelectedTopic, usePriorityStore } from "@/stores/priorityStore";
import { StyledMarkdown } from "@/ui";
import { useIsMobile } from "@/ui";
import { themePatternSurfaceStyle } from "@canucci/base-mantine/theme";
import { Alert, Anchor, Box, Breadcrumbs, Text, Title, useMantineTheme } from "@mantine/core";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { type FocusAreaData } from "../../services/apitypes";
import { FocusAreaDetailView } from "./FocusArea/FocusSection";
import { shouldUseLegacyCanvasPresentation } from "./canvasPresentation";
import { focusBackgroundAtIndex } from "@/utils/focusPatterns";

function FocusAreaPage() {
  const theme = useMantineTheme();
  const { sizes, spacing, zIndex } = theme.other;
  const { setDarkBackground } = useCanvasLayout();
  const { isParticipation } = useCanvas();
  const {
    canvasId,
    groupIndex: groupIndexStr,
    areaIndex: areaIndexStr,
  } = useParams<{
    canvasId: string;
    groupIndex: string;
    areaIndex: string;
  }>();
  const { t } = useTranslation(["pages", "common"]);
  const navigate = useNavigate();

  // Use granular selectors for performance
  const selectedTopicIds = usePriorityStore((state) => state.selectedTopicIds);
  const { toggleTopic } = usePriorityStore.getState();

  const parentPath = canvasId ? routes.canvas.overview.path(canvasId, isParticipation) : undefined;

  useCanvasParentPath(parentPath);
  const groupIndex = groupIndexStr ? parseInt(groupIndexStr, 10) : NaN;
  const areaIndex = areaIndexStr ? parseInt(areaIndexStr, 10) : NaN;

  const isValidIndices = !isNaN(groupIndex) && !isNaN(areaIndex);
  const currentCarouselIndex = !isNaN(areaIndex) ? areaIndex : 0;
  const isMobile = useIsMobile();

  const {
    canvasResult: { data: canvasData },
  } = useCanvas();
  const useLegacyPresentation = shouldUseLegacyCanvasPresentation(canvasData);
  useCanvasTitle(isMobile ? (canvasData?.themes?.[groupIndex]?.text.title ?? undefined) : undefined);
  useCanvasSubtitle(undefined);

  useEffect(() => {
    setDarkBackground(true);
  }, [setDarkBackground]);

  const { votingEnabled, isVotingClosed } = useVotingStatus();
  const handlePageChange = useCallback(
    (index: number) => {
      if (canvasId && parentPath) {
        const newPath = routes.canvas.focusArea.path(canvasId, groupIndex, index, isParticipation);
        navigate(newPath, { viewTransition: true });
      }
    },
    [canvasId, groupIndex, isParticipation, navigate, parentPath],
  );

  let focusAreaData: FocusAreaData | null = null;
  let dataError: string | null = null;
  let carouselItems: CarouselItem[] = [];
  let groupTitle: string | null = null;
  let groupSummary: string | null = null;

  if (!isValidIndices) {
    dataError = t("canvas.focusArea.invalidIndexError");
  } else if (canvasData) {
    const group = canvasData.themes?.[groupIndex];
    if (group) {
      groupTitle = group.text.title ?? t("canvas.focusArea.groupFallback", { index: groupIndex + 1 });
      groupSummary = group.text.summary ?? group.text.body ?? null;
      focusAreaData = group.subthemes?.[areaIndex] ?? null;
      carouselItems = (group.subthemes ?? []).map((focusArea) => {
        return {
          title: focusArea.text.title ?? "",
          description: focusArea.text.summary ?? focusArea.text.body ?? "",
        };
      });
      if (!focusAreaData) {
        dataError = t("canvas.focusArea.areaNotFoundError");
      }
    } else {
      dataError = t("canvas.focusArea.groupNotFoundError");
    }
  } else if (!canvasData) {
    dataError = t("errors.generic.message", { ns: "pages" });
  }

  if (dataError) {
    return (
      <Alert color="yellow" title={t("errors.generic.title", { ns: "pages" })}>
        {dataError}
      </Alert>
    );
  }

  // Star toggle logic
  const topicId = focusAreaData?.id ?? createTopicId(groupIndex, areaIndex);
  const isStarSelected = selectedTopicIds.includes(topicId);

  const showGroupInfo = false;

  const handleStarToggle = () => {
    // Event propagation is handled inside the StarToggle component.
    if (isVotingClosed) {
      return;
    }
    if (focusAreaData && groupTitle) {
      const topic: SelectedTopic = {
        topicId,
        groupIndex,
        areaIndex,
        title: focusAreaData.text.title ?? t("canvas.focusArea.pageTitleFallback"),
        groupTitle,
        label: groupTitle,
      };
      toggleTopic(topic);
    }
  };

  console.log("votingEnabled", votingEnabled);
  console.log("isVotingClosed", isVotingClosed);

  return (
    <Box w="100%" mih="100%">
      <Box pb="2xl" maw={sizes.maxPageWidthDesktop} mx="auto">
        {!isMobile && (
          <Box
            c={theme.other.semanticColors.invertedText}
            style={{ textAlign: "left", position: "relative" }}
            px="xl"
            py="lg"
          >
            <Box>
              <Breadcrumbs
                separator={
                  <Text span c="dimmed">
                    &gt;
                  </Text>
                }
                styles={{
                  separator: {
                    color: "rgba(255, 255, 255, 0.7)",
                    marginLeft: "var(--mantine-spacing-xs)",
                    marginRight: "var(--mantine-spacing-xs)",
                  },
                }}
              >
                <Anchor
                  component="button"
                  c="rgba(255, 255, 255, 0.8)"
                  fw="bold"
                  fz="sm"
                  onClick={() => navigate(parentPath!)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  {t("canvas.overview.backToOverview")}
                </Anchor>
                <Text span c="white" fw="bold" fz="sm">
                  {groupTitle}
                </Text>
              </Breadcrumbs>
            </Box>
            {showGroupInfo && (
              <>
                <Title size="h1" mt="md" pb="xs" maw={sizes.maxNarrowPageWidthDesktop}>
                  {groupTitle}
                </Title>
                <Box maw={sizes.maxNarrowPageWidthDesktop} fs="normal" fz="lg">
                  <StyledMarkdown content={groupSummary || ""} />
                </Box>
              </>
            )}
          </Box>
        )}
        <Box style={{ position: "relative" }}>
          <Carousel
            items={carouselItems}
            backgroundStyle={themePatternSurfaceStyle(focusBackgroundAtIndex(groupIndex), groupIndex)}
            currentIndex={currentCarouselIndex}
            onCurrentIndexChange={handlePageChange}
            showTitlePre={!useLegacyPresentation}
          />
          {votingEnabled && focusAreaData && (
            <Box
              pos="absolute"
              style={{
                // Dynamic positioning based on mobile state
                top: isMobile ? theme.spacing.xs : theme.spacing.xs,
                right: isMobile ? theme.spacing.xs : theme.spacing.xl,
                zIndex: zIndex.sticky,
              }}
            >
              <OverlayStarToggle
                isSelected={isStarSelected}
                onToggle={handleStarToggle}
                disabled={isVotingClosed}
                withGlass
              />
            </Box>
          )}
        </Box>
        <Box px={spacing.sectionGap}>
          <FocusAreaDetailView
            title={focusAreaData?.text.title || t("canvas.focusArea.pageTitleFallback")}
            content={focusAreaData?.text.body || focusAreaData?.text.summary || ""}
            quotes={focusAreaData?.quotes?.map((quote) => quote.text) ?? []}
            minHeight={isMobile ? undefined : "24rem"}
            spacing={spacing.sectionGap}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default FocusAreaPage;
